// ============================================================================
// /time/pay-run — VIEW FOUR. HR, Finance and Exec look at a pay period, adjust
// what it shows, see what is wrong with it, and then export.
//
// ⛔ OPENING THIS SCREEN RECORDS NOTHING. The versioned export ledger is
// written on ONE call — the Export button — and nowhere else on any of the four
// views. A view is a view; only the export is a payroll artifact.
//
// ⛔ Each person's nights come from the SAME function behind the worker's own
// card on /clock, so what HR reads here and what the worker reads on their
// phone cannot disagree about a night or a rate.
//
// ⛔ D-05: a worker whose rate is not verified renders HOURS and a sentence.
// Never a zero, never a blank. ⛔ D-01: no overtime exists anywhere.
// ============================================================================

import { canRunPayAndReports } from '../../shared/role-gates';
import { useEffect, useMemo, useState } from 'react';
import { Button, PageHeader, TabBar } from '../../shared/components';
import { useAuth } from '../../shared/auth';
import { errorMessage } from '../../shared/errors';
import { useTimeTabsNav } from './time-console';
import { ExportButtons } from './export-buttons';
import type { CellValue } from './export-file';
import { runPayRun, usePayPeriods } from './hooks/use-time-data';
import type { PayRunPayload, PayRunPerson, WorkerHoursRow } from './types';
import { formatSiteTime } from '../../lib/siteTime';

/** The switches, in one place. Each changes the TABLE and nothing else. */
type Grouping = 'flat' | 'building' | 'market';

function fmtDay(d: string): string {
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function fmtRange(a: string, b: string): string {
  const f = (s: string) => {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  return `${f(a)} – ${f(b)}`;
}

/** ⛔ The worker's clock, labelled ("7:52 PM CT") — never Kansas City's and
 *  never the viewer's. Antonela reads this from Madrid (Ben, 2026-09-24). */
function fmtTime(iso: string | null, tz: string | null): string {
  if (!iso) return '—';
  return formatSiteTime(iso, tz);
}

function money(n: number | null | undefined): string {
  if (n == null) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/** Which building or market a night belongs to, for the grouping switch. */
function groupOf(row: WorkerHoursRow, by: Grouping): string {
  if (by === 'flat') return '';
  const place = row.place_display ?? 'Not recorded';
  if (by === 'building') return place.startsWith('Market: ') ? 'No building recorded' : place;
  return place.startsWith('Market: ') ? place.slice('Market: '.length) : place;
}

function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick}
      className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ${on
        ? 'bg-tcs-inverse text-tcs-inverse-ink'
        : 'border border-tcs-border-strong bg-tcs-bg text-tcs-text hover:bg-tcs-surface'}`}>
      {children}
    </button>
  );
}

export function TimePayRun() {
  const { profile } = useAuth();
  const tabsNav = useTimeTabsNav('pay-run');
  const periods = usePayPeriods();

  const role = profile?.role ?? 'none';
  const canRun = canRunPayAndReports(role);

  // ----- the switches -----
  const [periodIdx, setPeriodIdx] = useState<number | null>(null);
  const [detail, setDetail] = useState(true);
  const [showMoney, setShowMoney] = useState(true);
  const [grouping, setGrouping] = useState<Grouping>('flat');
  const [onlyWorker, setOnlyWorker] = useState<string | null>(null);

  const [run, setRun] = useState<PayRunPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exported, setExported] = useState<PayRunPayload['export']>(null);

  // The period we are in, not the newest one on the calendar.
  const chosen = useMemo(() => {
    if (periods.rows.length === 0) return null;
    if (periodIdx != null) return periods.rows[periodIdx] ?? null;
    const today = new Date().toISOString().slice(0, 10);
    return periods.rows.find((p) => p.period_start <= today && p.period_end >= today) ?? periods.rows[0];
  }, [periods.rows, periodIdx]);

  // ⛔ Reading the period is a READ. Nothing here writes, including this effect.
  useEffect(() => {
    let cancelled = false;
    if (!chosen || !canRun) return;
    setLoading(true);
    setExported(null);
    void (async () => {
      try {
        const r = await runPayRun(chosen.period_start, chosen.period_end, false);
        if (cancelled) return;
        setRun(r);
        setFailed(null);
      } catch (e) {
        if (cancelled) return;
        setFailed(errorMessage(e, 'Could not build the pay run.'));
        setRun(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [chosen, canRun]);

  const people: PayRunPerson[] = useMemo(() => {
    const all = run?.people ?? [];
    return onlyWorker ? all.filter((p) => p.worker_id === onlyWorker) : all;
  }, [run, onlyWorker]);

  const totals = useMemo(() => ({
    hours: people.reduce((s, p) => s + p.hours_total, 0),
    amount: people.reduce((s, p) => s + (p.amount_estimate ?? 0), 0),
    unpriced: people.filter((p) => p.hours_total > 0 && p.amount_estimate == null).length,
  }), [people]);

  /**
   * The pay run as a file, built once for all three formats.
   *
   * ⛔ D-05 — an unverified rate renders HOURS and never a zero. A blank in
   * the Amount column means "nobody has verified this rate", which is not the
   * same fact as $0.00 and must never be flattened into one.
   *
   * ⛔ Downloading is a VIEW and records nothing (TCSD-0300). The file carries
   * the export id only when this period has actually been stamped, so a sheet
   * that came off an unexported run cannot be mistaken for a payroll artifact.
   */
  function payRunRows(): CellValue[][] {
    if (!run) return [];
    const stamp = exported ? `${exported.export_id} v${exported.version}` : 'NOT EXPORTED — reference only';
    return [
      [`TCS pay run — ${run.range_start} to ${run.range_end} · ${stamp}`],
      ['Section', 'Worker', 'Position', 'Market', 'Paid by', 'Hours', 'Rate', 'Amount', 'Hours with no verified rate'],
      ...people.map((p) => [
        p.section, p.worker_name, p.position_title ?? '', p.position_market ?? '', p.paid_by,
        p.hours_total, p.rate_display ?? '', p.amount_estimate ?? '', p.unrated_hours || '',
      ]),
      ['TOTAL', '', '', '', '', totals.hours, '', totals.amount || '', ''],
    ];
  }

  async function doExport() {
    if (!chosen) return;
    setExporting(true);
    setFailed(null);
    try {
      // ⛔ THE ONLY WRITE ON ANY OF THE FOUR VIEWS.
      const r = await runPayRun(chosen.period_start, chosen.period_end, true);
      setRun(r);
      setExported(r.export);
    } catch (e) {
      setFailed(errorMessage(e, 'The export did not go through.'));
    } finally {
      setExporting(false);
    }
  }

  if (!canRun) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-6">
        <PageHeader eyebrow="Time & Labor" title="Pay run"
          meta="HR, finance and executives run payroll." />
        <div className="mt-3"><TabBar {...tabsNav} /></div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <PageHeader
        eyebrow="Time & Labor"
        title="Pay run"
        meta="What we pay everybody this period, and anything wrong with it. Looking at this page records nothing — only Export does."
      />
      <div className="mt-3"><TabBar {...tabsNav} /></div>

      {/* ===== the switches ===== */}
      <section className="mt-5 rounded-xl border border-tcs-border bg-tcs-raised p-4">
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={periodIdx ?? (chosen ? periods.rows.indexOf(chosen) : 0)}
            onChange={(e) => setPeriodIdx(Number(e.target.value))}
            aria-label="Pay period"
            className="h-9 rounded-lg border border-tcs-border-input bg-tcs-field px-2 text-sm font-semibold text-tcs-text">
            {periods.rows.map((p, i) => (
              <option key={p.period_id} value={i}>{fmtRange(p.period_start, p.period_end)}</option>
            ))}
          </select>

          <select
            value={onlyWorker ?? ''}
            onChange={(e) => setOnlyWorker(e.target.value === '' ? null : e.target.value)}
            aria-label="Who"
            className="h-9 max-w-[240px] rounded-lg border border-tcs-border-input bg-tcs-field px-2 text-sm text-tcs-text">
            <option value="">Everybody</option>
            {(run?.people ?? []).map((p) => (
              <option key={p.worker_id} value={p.worker_id}>{p.worker_name}</option>
            ))}
          </select>

          <Chip on={detail} onClick={() => setDetail((v) => !v)}>Daily detail</Chip>
          <Chip on={showMoney} onClick={() => setShowMoney((v) => !v)}>Rate &amp; amount</Chip>

          <select value={grouping} onChange={(e) => setGrouping(e.target.value as Grouping)}
            aria-label="Group by"
            className="h-9 rounded-lg border border-tcs-border-input bg-tcs-field px-2 text-sm text-tcs-text">
            <option value="flat">Group: flat</option>
            <option value="building">Group: by building</option>
            <option value="market">Group: by market</option>
          </select>
        </div>
        {periods.error && <div className="mt-2 text-sm text-st-danger-fg">{periods.error}</div>}
        <div className="mt-2 text-xs text-tcs-text-secondary">
          Sub crews are paid on the weekly sub payment sheets and are deliberately not on this screen.
        </div>
      </section>

      {loading && <div className="mt-4 text-sm text-tcs-text-muted">Loading…</div>}
      {failed && <div className="mt-4 text-sm text-st-danger-fg">{failed}</div>}

      {/* ===== anything unresolved: SHOWN, and blocking nothing ===== */}
      {run && run.warnings.length > 0 && (
        <div className="mt-4 rounded-xl border border-st-warn-border bg-st-warn-bg px-4 py-3">
          <div className="text-sm font-bold text-st-warn-fg">
            {run.warnings.length} night{run.warnings.length === 1 ? '' : 's'} in this period {run.warnings.length === 1 ? 'has' : 'have'} not been answered.
          </div>
          <ul className="mt-1 flex flex-col gap-1">
            {run.warnings.map((w) => (
              <li key={w.exception_id} className="text-[12.5px] text-st-warn-fg">
                <b>{w.worker_name}</b>, {fmtDay(w.shift_date)} — clocked in {fmtTime(w.started_at, w.site_tz)} at {w.place_display},
                {' '}no clock-out, closed at {w.hours.toFixed(2)} h.
              </li>
            ))}
          </ul>
          <div className="mt-1 text-[11.5px] text-st-warn-fg">
            This does not stop the export. It is here so nobody exports a period without having seen it.
          </div>
        </div>
      )}

      {/* ===== the run ===== */}
      {run && (
        <div className="mt-4 overflow-x-auto rounded-xl border border-tcs-border">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-tcs-surface text-left text-[11px] uppercase tracking-wide text-tcs-text-muted">
                <th className="px-3 py-2 font-semibold">Person</th>
                <th className="px-3 py-2 font-semibold">Paid by</th>
                {detail && <th className="px-3 py-2 font-semibold">Night</th>}
                {detail && <th className="px-3 py-2 font-semibold">In (worker&rsquo;s time)</th>}
                {detail && <th className="px-3 py-2 font-semibold">Out (worker&rsquo;s time)</th>}
                <th className="px-3 py-2 text-right font-semibold">Hours</th>
                {showMoney && <th className="px-3 py-2 text-right font-semibold">Rate</th>}
                {showMoney && <th className="px-3 py-2 text-right font-semibold">Amount</th>}
              </tr>
            </thead>
            <tbody>
              {people.map((p) => {
                const cols = 2 + (detail ? 3 : 0) + 1 + (showMoney ? 2 : 0);
                const groups = detail
                  ? Array.from(new Set(p.days.map((d) => groupOf(d, grouping))))
                  : [];
                return (
                  <>
                    <tr key={p.worker_id} className="border-t border-tcs-border bg-tcs-raised">
                      <td className="px-3 py-2">
                        <div className="font-semibold text-tcs-text">{p.worker_name}</div>
                        {p.position_title && (
                          <div className="text-[11px] text-tcs-text-muted">
                            {p.position_title}{p.position_market ? ` · ${p.position_market}` : ''}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-tcs-text-secondary">{p.paid_by}</td>
                      {detail && <td className="px-3 py-2 text-tcs-text-muted" colSpan={3}>
                        {p.days.length === 0
                          ? <span className="italic">no nights this period</span>
                          : `${p.days.length} night${p.days.length === 1 ? '' : 's'}`}
                      </td>}
                      <td className="px-3 py-2 text-right font-semibold tabular-nums">
                        {p.hours_total > 0 ? p.hours_total.toFixed(2) : '—'}
                      </td>
                      {showMoney && (
                        <td className="px-3 py-2 text-right tabular-nums">
                          {/* ⛔ D-05 — hours and a sentence, never a zero. */}
                          {p.rate_display != null
                            ? money(p.rate_display)
                            : <span className="text-st-warn-fg">no rate on file</span>}
                        </td>
                      )}
                      {showMoney && (
                        <td className="px-3 py-2 text-right tabular-nums">{money(p.amount_estimate)}</td>
                      )}
                    </tr>

                    {detail && groups.map((g) => (
                      <>
                        {grouping !== 'flat' && (
                          <tr key={`${p.worker_id}-${g}-h`} className="border-t border-tcs-border bg-tcs-surface">
                            <td className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted"
                                colSpan={cols}>
                              {g}
                            </td>
                          </tr>
                        )}
                        {p.days.filter((d) => groupOf(d, grouping) === g).map((d) => (
                          <tr key={`${p.worker_id}-${d.segment_id}`} className="border-t border-tcs-border bg-tcs-raised">
                            <td className="px-3 py-1.5" />
                            <td className="px-3 py-1.5 text-[12px] text-tcs-text-muted">{d.place_display}</td>
                            <td className="px-3 py-1.5 text-[12px] text-tcs-text-secondary">{fmtDay(d.shift_date)}</td>
                            <td className="px-3 py-1.5 text-[12px] tabular-nums">{fmtTime(d.in_at, d.site_tz)}</td>
                            <td className="px-3 py-1.5 text-[12px] tabular-nums">
                              {d.open_exception
                                ? <span className="font-semibold text-st-danger-fg">— none —</span>
                                : fmtTime(d.out_at, d.site_tz)}
                            </td>
                            <td className="px-3 py-1.5 text-right text-[12px] tabular-nums">{d.hours.toFixed(2)}</td>
                            {showMoney && (
                              <td className="px-3 py-1.5 text-right text-[12px] tabular-nums">
                                {d.rate != null ? money(d.rate) : <span className="text-st-warn-fg">unverified</span>}
                              </td>
                            )}
                            {showMoney && (
                              <td className="px-3 py-1.5 text-right text-[12px] tabular-nums">{money(d.amount)}</td>
                            )}
                          </tr>
                        ))}
                      </>
                    ))}
                  </>
                );
              })}

              <tr className="border-t-2 border-tcs-border-strong bg-tcs-surface font-bold">
                <td className="px-3 py-2" colSpan={detail ? 5 : 2}>
                  Period total — TCS-paid
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{totals.hours.toFixed(2)}</td>
                {showMoney && <td className="px-3 py-2" />}
                {showMoney && (
                  <td className="px-3 py-2 text-right tabular-nums">
                    {money(totals.amount)}
                    {totals.unpriced > 0 && (
                      <div className="text-[10.5px] font-normal text-st-warn-fg">
                        + {totals.unpriced} {totals.unpriced === 1 ? 'person' : 'people'} hours-only
                      </div>
                    )}
                  </td>
                )}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {run && people.length === 0 && (
        <div className="mt-3 rounded-xl border border-dashed border-tcs-border-strong px-4 py-8 text-center">
          <div className="text-sm font-semibold text-tcs-text">Nobody on our own payroll has hours in this period.</div>
          <div className="mx-auto mt-1 max-w-prose text-xs text-tcs-text-secondary">
            An ordinary answer, not a fault — the punch clock is still being rolled out. Sub crews are
            paid on the weekly sub payment sheets, which are on the Payroll &amp; reports screen.
          </div>
        </div>
      )}

      {/* ===== the one export ===== */}
      {run && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button loading={exporting} onClick={() => void doExport()}>Export for payroll</Button>
          <span className="max-w-prose text-[11.5px] text-tcs-text-secondary">
            Stamped and numbered when pressed, against this exact set of numbers. Exporting the same
            period again is version 2, and both stay. ⛔ This is the only button on any of the four
            views that records anything.
          </span>
        </div>
      )}

      {/* The FILE, in the three formats HR asked for. ⛔ Downloading records
          nothing — the sheet says so in its own header until the period has
          been stamped, so a reference copy can never be mistaken for one. */}
      {run && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-tcs-text-muted">
            Take it with you
          </span>
          <ExportButtons
            baseFilename={`pay-run-${run.range_start}-to-${run.range_end}`}
            sheetName={`Pay run ${run.range_start}`}
            rows={payRunRows}
            hint={exported ? undefined : 'not exported yet — the file says so'}
          />
        </div>
      )}

      {exported && run && (
        <div className="mt-3 rounded-xl border border-st-ok-border bg-st-ok-bg px-4 py-3 text-sm text-st-ok-fg">
          Exported as <b>{exported.export_id}</b>, version {exported.version}
          {exported.supersedes && <> — the earlier export of this period stays on file</>}
          {exported.period_id && <> · linked to the {fmtRange(run.range_start, run.range_end)} period</>}.
          {' '}It is on the Payroll &amp; reports screen with every other artifact.
        </div>
      )}
    </div>
  );
}
